/**
 * Cricket Sprites - Ceefax/Teletext-style block graphic sprite generator
 *
 * Chunky 2×2 pixel blocks on the classic Teletext 8-color palette
 * for an authentic retro broadcast look.
 *
 * Animations:
 * - Idle (standing)
 * - Running (4 frames)
 * - Bowling action (4 frames)
 * - Batting swing (3 frames)
 * - Fielding/catching
 */

// Sprite dimensions
const SPRITE_WIDTH = 16;
const SPRITE_HEIGHT = 24;
const SCALE = 2;

// Ceefax/Teletext 8-color palette
const COLORS = {
  skin: '#ffff00',        // Yellow - classic Teletext skin
  skinShadow: '#ffff00',  // No shadows in Teletext
  hairBrown: '#ff0000',   // Red (closest to brown)
  hairBlack: '#000000',
  hairBlond: '#ffff00',   // Yellow
  white: '#ffffff',
  black: '#000000',
  shorts: '#0000ff',      // Blue
  shortsLight: '#0000ff',
  bat: '#cc8800',         // Bright wood brown
  batHandle: '#ffff00',   // Yellow
  gloves: '#ffff00',      // Yellow
  ball: '#ff0000'         // Red
};

/**
 * Create a sprite sheet for a player
 * All coordinates use 2×2 block grid for Teletext aesthetic
 */
function createPlayerSpriteSheet(shirtColor, hairColor = COLORS.hairBrown, playerType = 'fielder') {
  const framesPerRow = 8;
  const rows = 4;

  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_WIDTH * framesPerRow * SCALE;
  canvas.height = SPRITE_HEIGHT * rows * SCALE;

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const colors = {
    shirt: shirtColor,
    hair: hairColor,
    skin: COLORS.skin,
    shorts: COLORS.shorts,
    white: COLORS.white
  };

  // Row 0: Idle right (0), Idle left (1), Run right (2-5)
  drawIdleRight(ctx, 0, 0, colors);
  drawIdleLeft(ctx, 1, 0, colors);
  drawRunRight(ctx, 2, 0, 0, colors);
  drawRunRight(ctx, 3, 0, 1, colors);
  drawRunRight(ctx, 4, 0, 2, colors);
  drawRunRight(ctx, 5, 0, 3, colors);

  // Row 1: Run left (0-3)
  drawRunLeft(ctx, 0, 1, 0, colors);
  drawRunLeft(ctx, 1, 1, 1, colors);
  drawRunLeft(ctx, 2, 1, 2, colors);
  drawRunLeft(ctx, 3, 1, 3, colors);

  // Row 2: Bowling (0-3)
  drawBowlFrame(ctx, 0, 2, 0, colors);
  drawBowlFrame(ctx, 1, 2, 1, colors);
  drawBowlFrame(ctx, 2, 2, 2, colors);
  drawBowlFrame(ctx, 3, 2, 3, colors);

  // Row 3: Batting (0-2), Catching (3)
  drawBatFrame(ctx, 0, 3, 0, colors);
  drawBatFrame(ctx, 1, 3, 1, colors);
  drawBatFrame(ctx, 2, 3, 2, colors);
  drawCatchFrame(ctx, 3, 3, colors);

  return canvas;
}

// Draw a block-aligned rectangle
function drawRect(ctx, x, y, w, h, color, frameX = 0, frameY = 0) {
  ctx.fillStyle = color;
  const px = (frameX * SPRITE_WIDTH + x) * SCALE;
  const py = (frameY * SPRITE_HEIGHT + y) * SCALE;
  ctx.fillRect(px, py, w * SCALE, h * SCALE);
}

// === IDLE SPRITES ===

function drawIdleRight(ctx, frameX, frameY, colors) {
  drawRect(ctx, 6, 0, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 2, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 6, 6, 6, colors.shirt, frameX, frameY);
  drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 12, 6, 2, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 12, 6, 2, colors.shorts, frameX, frameY);
  drawRect(ctx, 6, 14, 2, 6, colors.skin, frameX, frameY);
  drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 20, 2, 2, colors.white, frameX, frameY);
  drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
}

function drawIdleLeft(ctx, frameX, frameY, colors) {
  drawRect(ctx, 6, 0, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 2, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 6, 6, 6, colors.shirt, frameX, frameY);
  drawRect(ctx, 2, 6, 2, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 10, 6, 2, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 12, 6, 2, colors.shorts, frameX, frameY);
  drawRect(ctx, 4, 14, 2, 6, colors.skin, frameX, frameY);
  drawRect(ctx, 8, 14, 2, 6, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
  drawRect(ctx, 8, 20, 2, 2, colors.white, frameX, frameY);
}

// === RUNNING SPRITES ===

function drawRunRight(ctx, frameX, frameY, frame, colors) {
  drawRect(ctx, 6, 0, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 2, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 6, 6, 6, colors.shirt, frameX, frameY);
  drawRect(ctx, 6, 12, 6, 2, colors.shorts, frameX, frameY);

  if (frame === 0) {
    drawRect(ctx, 12, 6, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 8, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 10, 18, 2, 2, colors.white, frameX, frameY);
  } else if (frame === 1) {
    drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 6, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 6, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
  } else if (frame === 2) {
    drawRect(ctx, 4, 6, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 8, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 4, 18, 2, 2, colors.white, frameX, frameY);
  } else {
    drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 6, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 6, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
  }
}

function drawRunLeft(ctx, frameX, frameY, frame, colors) {
  drawRect(ctx, 6, 0, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 2, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 6, 6, 6, colors.shirt, frameX, frameY);
  drawRect(ctx, 4, 12, 6, 2, colors.shorts, frameX, frameY);

  if (frame === 0) {
    drawRect(ctx, 2, 6, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 8, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 4, 18, 2, 2, colors.white, frameX, frameY);
  } else if (frame === 1) {
    drawRect(ctx, 2, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 8, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 8, 20, 2, 2, colors.white, frameX, frameY);
  } else if (frame === 2) {
    drawRect(ctx, 10, 6, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 2, 8, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 10, 18, 2, 2, colors.white, frameX, frameY);
  } else {
    drawRect(ctx, 2, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 8, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 8, 20, 2, 2, colors.white, frameX, frameY);
  }
}

// === BOWLING SPRITES ===

function drawBowlFrame(ctx, frameX, frameY, frame, colors) {
  drawRect(ctx, 6, 0, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 2, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 6, 6, 6, colors.shirt, frameX, frameY);
  drawRect(ctx, 6, 12, 6, 2, colors.shorts, frameX, frameY);

  if (frame === 0) {
    // Wind up - arms at sides, ball in hand
    drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 10, 2, 2, COLORS.ball, frameX, frameY);
    drawRect(ctx, 6, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 6, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
  } else if (frame === 1) {
    // Arm going up
    drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 4, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 2, 2, 2, COLORS.ball, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 6, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 6, 18, 2, 2, colors.white, frameX, frameY);
  } else if (frame === 2) {
    // Arm at top
    drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 0, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 0, 2, 2, COLORS.ball, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 4, 18, 2, 2, colors.white, frameX, frameY);
  } else {
    // Follow through - ball released
    drawRect(ctx, 4, 6, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 6, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 14, 8, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 4, 18, 2, 2, colors.white, frameX, frameY);
  }
}

// === BATTING SPRITES ===

function drawBatFrame(ctx, frameX, frameY, frame, colors) {
  if (frame === 0) {
    // Ready stance - facing left, bat grounded
    drawRect(ctx, 4, 0, 4, 2, colors.hair, frameX, frameY);
    drawRect(ctx, 4, 2, 4, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 6, 6, 6, colors.shirt, frameX, frameY);
    drawRect(ctx, 4, 12, 6, 2, colors.shorts, frameX, frameY);
    drawRect(ctx, 4, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 8, 14, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 18, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 8, 18, 2, 2, colors.white, frameX, frameY);
    // Hands + bat grounded
    drawRect(ctx, 10, 8, 2, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 10, 10, 2, 2, COLORS.batHandle, frameX, frameY);
    drawRect(ctx, 10, 12, 2, 8, COLORS.bat, frameX, frameY);
  } else if (frame === 1) {
    // Backlift - bat raised behind
    drawRect(ctx, 4, 2, 4, 2, colors.hair, frameX, frameY);
    drawRect(ctx, 4, 4, 4, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 8, 6, 6, colors.shirt, frameX, frameY);
    drawRect(ctx, 4, 14, 6, 2, colors.shorts, frameX, frameY);
    drawRect(ctx, 4, 16, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 8, 16, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 8, 20, 2, 2, colors.white, frameX, frameY);
    // Arms raised + bat behind head
    drawRect(ctx, 10, 4, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 12, 4, 2, 2, COLORS.batHandle, frameX, frameY);
    drawRect(ctx, 12, 0, 2, 4, COLORS.bat, frameX, frameY);
  } else {
    // Shot played - bat swung through
    drawRect(ctx, 4, 2, 4, 2, colors.hair, frameX, frameY);
    drawRect(ctx, 4, 4, 4, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 4, 8, 6, 6, colors.shirt, frameX, frameY);
    drawRect(ctx, 4, 14, 6, 2, colors.shorts, frameX, frameY);
    drawRect(ctx, 2, 16, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 8, 16, 2, 4, colors.skin, frameX, frameY);
    drawRect(ctx, 2, 20, 2, 2, colors.white, frameX, frameY);
    drawRect(ctx, 8, 20, 2, 2, colors.white, frameX, frameY);
    // Arms extended + bat through
    drawRect(ctx, 0, 8, 4, 2, colors.skin, frameX, frameY);
    drawRect(ctx, 0, 6, 2, 2, COLORS.batHandle, frameX, frameY);
    drawRect(ctx, 0, 2, 2, 4, COLORS.bat, frameX, frameY);
  }
}

// === CATCHING SPRITE ===

function drawCatchFrame(ctx, frameX, frameY, colors) {
  drawRect(ctx, 6, 0, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 2, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 6, 6, 6, colors.shirt, frameX, frameY);
  drawRect(ctx, 6, 12, 6, 2, colors.shorts, frameX, frameY);
  // Arms stretched out
  drawRect(ctx, 0, 6, 6, 2, colors.skin, frameX, frameY);
  drawRect(ctx, 12, 6, 4, 2, colors.skin, frameX, frameY);
  // Legs bent wide
  drawRect(ctx, 4, 14, 2, 6, colors.skin, frameX, frameY);
  drawRect(ctx, 10, 14, 2, 6, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 20, 2, 2, colors.white, frameX, frameY);
  drawRect(ctx, 10, 20, 2, 2, colors.white, frameX, frameY);
}

// === WICKET KEEPER SPRITES ===

function createKeeperSpriteSheet(shirtColor, hairColor = COLORS.hairBlack) {
  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_WIDTH * 4 * SCALE;
  canvas.height = SPRITE_HEIGHT * 2 * SCALE;

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  const colors = {
    shirt: shirtColor,
    hair: hairColor,
    skin: COLORS.skin,
    shorts: COLORS.shorts,
    white: COLORS.white
  };

  drawKeeperCrouch(ctx, 0, 0, colors);
  drawKeeperDiveLeft(ctx, 1, 0, colors);
  drawKeeperDiveRight(ctx, 2, 0, colors);
  drawIdleLeft(ctx, 3, 0, colors);

  return canvas;
}

function drawKeeperCrouch(ctx, frameX, frameY, colors) {
  drawRect(ctx, 6, 4, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 6, 6, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 10, 8, 4, colors.shirt, frameX, frameY);
  drawRect(ctx, 4, 14, 8, 2, colors.shorts, frameX, frameY);
  // Crouched legs wide
  drawRect(ctx, 2, 16, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 10, 16, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 2, 20, 4, 2, colors.white, frameX, frameY);
  drawRect(ctx, 10, 20, 4, 2, colors.white, frameX, frameY);
  // Gloves out front
  drawRect(ctx, 0, 10, 4, 4, COLORS.gloves, frameX, frameY);
  drawRect(ctx, 12, 10, 4, 4, COLORS.gloves, frameX, frameY);
}

function drawKeeperDiveLeft(ctx, frameX, frameY, colors) {
  drawRect(ctx, 2, 8, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 2, 10, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 6, 10, 6, 4, colors.shirt, frameX, frameY);
  drawRect(ctx, 12, 10, 2, 2, colors.shorts, frameX, frameY);
  drawRect(ctx, 0, 8, 2, 4, COLORS.gloves, frameX, frameY);
  drawRect(ctx, 14, 12, 2, 4, colors.skin, frameX, frameY);
}

function drawKeeperDiveRight(ctx, frameX, frameY, colors) {
  drawRect(ctx, 10, 8, 4, 2, colors.hair, frameX, frameY);
  drawRect(ctx, 10, 10, 4, 4, colors.skin, frameX, frameY);
  drawRect(ctx, 4, 10, 6, 4, colors.shirt, frameX, frameY);
  drawRect(ctx, 2, 10, 2, 2, colors.shorts, frameX, frameY);
  drawRect(ctx, 14, 8, 2, 4, COLORS.gloves, frameX, frameY);
  drawRect(ctx, 0, 12, 2, 4, colors.skin, frameX, frameY);
}

// === BALL SPRITE ===

function createBallSprite() {
  const canvas = document.createElement('canvas');
  canvas.width = 6 * SCALE;
  canvas.height = 6 * SCALE;

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  // Solid red block with white seam - pure Teletext
  ctx.fillStyle = COLORS.ball;
  ctx.fillRect(0, 0, 6 * SCALE, 6 * SCALE);
  ctx.fillStyle = COLORS.white;
  ctx.fillRect(2 * SCALE, 2 * SCALE, 2 * SCALE, 2 * SCALE);

  return canvas;
}

// === WICKET SPRITES ===

function createWicketSprite(intact = true) {
  const canvas = document.createElement('canvas');
  canvas.width = 12 * SCALE;
  canvas.height = 20 * SCALE;

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;

  if (intact) {
    // Three stumps as blocky columns
    ctx.fillStyle = COLORS.white;
    ctx.fillRect(0, 4 * SCALE, 2 * SCALE, 16 * SCALE);
    ctx.fillRect(4 * SCALE, 4 * SCALE, 2 * SCALE, 16 * SCALE);
    ctx.fillRect(8 * SCALE, 4 * SCALE, 2 * SCALE, 16 * SCALE);
    // Bails - yellow bar across top
    ctx.fillStyle = COLORS.gloves;
    ctx.fillRect(0, 2 * SCALE, 10 * SCALE, 2 * SCALE);
  } else {
    // Stumps scattered
    ctx.fillStyle = COLORS.white;
    ctx.fillRect(0, 8 * SCALE, 2 * SCALE, 12 * SCALE);
    ctx.fillRect(4 * SCALE, 6 * SCALE, 2 * SCALE, 14 * SCALE);
    ctx.fillRect(8 * SCALE, 4 * SCALE, 2 * SCALE, 12 * SCALE);
    // Bails flying
    ctx.fillStyle = COLORS.gloves;
    ctx.fillRect(0, 0, 4 * SCALE, 2 * SCALE);
    ctx.fillRect(6 * SCALE, 0, 4 * SCALE, 2 * SCALE);
  }

  return canvas;
}

// === SPRITE ANIMATOR CLASS ===

class SpriteAnimator {
  constructor(spriteSheet, frameWidth, frameHeight, scale = SCALE) {
    this.spriteSheet = spriteSheet;
    this.frameWidth = frameWidth * scale;
    this.frameHeight = frameHeight * scale;
    this.scale = scale;
    this.animations = {};
    this.currentAnimation = null;
    this.currentFrame = 0;
    this.frameTimer = 0;
    this.fps = 8;
    this.playing = false;
    this.loop = true;
    this.onComplete = null;
  }

  addAnimation(name, row, startFrame, frameCount, fps = 8) {
    this.animations[name] = {
      row,
      startFrame,
      frameCount,
      fps
    };
  }

  play(animationName, loop = true, onComplete = null) {
    if (this.animations[animationName]) {
      this.currentAnimation = this.animations[animationName];
      this.currentFrame = 0;
      this.frameTimer = 0;
      this.fps = this.currentAnimation.fps;
      this.playing = true;
      this.loop = loop;
      this.onComplete = onComplete;
    }
  }

  stop() {
    this.playing = false;
  }

  update(deltaTime) {
    if (!this.playing || !this.currentAnimation) return;

    this.frameTimer += deltaTime;
    const frameInterval = 1000 / this.fps;

    if (this.frameTimer >= frameInterval) {
      this.frameTimer -= frameInterval;
      this.currentFrame++;

      if (this.currentFrame >= this.currentAnimation.frameCount) {
        if (this.loop) {
          this.currentFrame = 0;
        } else {
          this.currentFrame = this.currentAnimation.frameCount - 1;
          this.playing = false;
          if (this.onComplete) this.onComplete();
        }
      }
    }
  }

  draw(ctx, x, y) {
    if (!this.currentAnimation) return;

    const srcX = (this.currentAnimation.startFrame + this.currentFrame) * this.frameWidth;
    const srcY = this.currentAnimation.row * this.frameHeight;

    ctx.drawImage(
      this.spriteSheet,
      srcX, srcY,
      this.frameWidth, this.frameHeight,
      x, y,
      this.frameWidth, this.frameHeight
    );
  }

  getFrameData() {
    if (!this.currentAnimation) return { x: 0, y: 0 };
    return {
      x: (this.currentAnimation.startFrame + this.currentFrame) * this.frameWidth,
      y: this.currentAnimation.row * this.frameHeight,
      width: this.frameWidth,
      height: this.frameHeight
    };
  }
}

// === GAME ENTITY CLASS ===

class CricketPlayer {
  constructor(spriteSheet, x, y, type = 'fielder') {
    this.x = x;
    this.y = y;
    this.targetX = x;
    this.targetY = y;
    this.type = type;
    this.speed = 100;
    this.direction = 'right';
    this.state = 'idle';

    this.boundary = { left: 45, right: 355, top: 50, bottom: 250 };

    this.animator = new SpriteAnimator(spriteSheet, SPRITE_WIDTH, SPRITE_HEIGHT);

    this.animator.addAnimation('idleRight', 0, 0, 1, 1);
    this.animator.addAnimation('idleLeft', 0, 1, 1, 1);
    this.animator.addAnimation('runRight', 0, 2, 4, 10);
    this.animator.addAnimation('runLeft', 1, 0, 4, 10);
    this.animator.addAnimation('bowl', 2, 0, 4, 6);
    this.animator.addAnimation('bat', 3, 0, 3, 8);
    this.animator.addAnimation('batReady', 3, 0, 1, 1);
    this.animator.addAnimation('catch', 3, 3, 1, 1);

    this.animator.play('idleRight');
  }

  moveTo(x, y, onComplete = null) {
    this.targetX = x;
    this.targetY = y;
    this.state = 'moving';
    this.onMoveComplete = onComplete;

    if (x > this.x) {
      this.direction = 'right';
      this.animator.play('runRight');
    } else if (x < this.x) {
      this.direction = 'left';
      this.animator.play('runLeft');
    }
  }

  playAction(action, onComplete = null) {
    this.state = 'action';
    this.animator.play(action, false, () => {
      this.state = 'idle';
      this.animator.play(this.direction === 'right' ? 'idleRight' : 'idleLeft');
      if (onComplete) onComplete();
    });
  }

  update(deltaTime) {
    this.animator.update(deltaTime);

    if (this.state === 'moving') {
      const dx = this.targetX - this.x;
      const dy = this.targetY - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 1) {
        this.x = this.targetX;
        this.y = this.targetY;
        this.state = 'idle';
        this.animator.play(this.direction === 'right' ? 'idleRight' : 'idleLeft');
        if (this.onMoveComplete) {
          this.onMoveComplete();
          this.onMoveComplete = null;
        }
      } else {
        const moveSpeed = this.speed * (deltaTime / 1000);
        const easeFactor = Math.min(1, dist / 30);
        const actualSpeed = moveSpeed * (0.5 + easeFactor * 0.5);

        this.x += (dx / dist) * actualSpeed;
        this.y += (dy / dist) * actualSpeed;
      }

      this.x = Math.max(this.boundary.left, Math.min(this.boundary.right, this.x));
      this.y = Math.max(this.boundary.top, Math.min(this.boundary.bottom, this.y));
    }
  }

  setBoundary(boundary) {
    this.boundary = boundary;
  }

  draw(ctx) {
    this.animator.draw(ctx, this.x - this.animator.frameWidth / 2, this.y - this.animator.frameHeight);
  }
}

// === EXPORTS ===

export {
  createPlayerSpriteSheet,
  createKeeperSpriteSheet,
  createBallSprite,
  createWicketSprite,
  SpriteAnimator,
  CricketPlayer,
  SPRITE_WIDTH,
  SPRITE_HEIGHT,
  SCALE,
  COLORS
};
